import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PAYMENT_METHODS, PAYMENT_METHOD_KEYS, SaleError, formatQty, parseQty, uuidv7, type PaymentMethod, type UnitType } from '@pos/core';
import { recordSaleLocally, type SaleRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Badge, Button, Card, Input, PageHeader, Select, Spinner, Stat, cx, errorMessage } from '../../ui/index.tsx';
import { CashIcon, ReceiptIcon, TrendIcon } from '../../ui/icons.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { buildReturn, mainMethod, returnableQty } from '../pos/cart.ts';
import { usePosSettings } from '../pos/state.ts';
import { useOpenSession } from '../cash/data.ts';
import { lineDetail } from '../pos/ticket.ts';
import { useTicket } from '../pos/useTicket.ts';
import { PERIODS, fetchReturns, periodStart, useSalesList, type Period } from './data.ts';
import { useCanWrite } from '../../services/license.ts';
import { ReadOnlyNotice } from '../license/LicenseNotices.tsx';

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

/** Historial de ventas y devoluciones: reimprimir, compartir y devolver. */
export function SalesPage() {
  const { business, membership, can, isOwner } = useSession();
  const canWrite = useCanWrite();
  const catalog = useCatalog();
  const money = useMoney();
  const [period, setPeriod] = useState<Period>('today');
  const [number, setNumber] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const members = useLiveQuery(() => db.memberships.where('business_id').equals(business.id).toArray(), [business.id]);

  const unitOf = useCallback(
    (variantId: string): UnitType => {
      for (const item of catalog?.items ?? []) if (item.variants.some((v) => v.id === variantId)) return item.product.unit_type;
      return 'unit';
    },
    [catalog],
  );
  const from = useMemo(() => periodStart(period), [period]);
  // Sin "anular ventas", un empleado ve solo sus ventas (igual que en el servidor).
  const onlyOwn = can('void_sales') || membership.role === 'owner' ? null : membership.user_id;
  const list = useSalesList({ from, number }, unitOf, onlyOwn);

  const summary = useMemo(() => {
    let sold = 0;
    let returned = 0;
    let count = 0;
    for (const s of list.sales) {
      if (s.kind === 'sale') {
        sold += s.total;
        count++;
      } else returned += s.total;
    }
    return { sold, returned, count };
  }, [list.sales]);

  const person = (id: string) => members?.find((m) => m.user_id === id)?.display_name ?? '—';
  const open = openId ? list.sales.find((s) => s.id === openId) : undefined;

  // Modo consulta: los empleados no ven las ventas (el dueño sí, sin poder hacer devoluciones).
  if (!canWrite && !isOwner) return <ReadOnlyNotice />;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Ventas" description="Ventas y devoluciones. Tocá una para ver el detalle, reimprimir el ticket o hacer una devolución." />

      <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
        <Select value={period} onChange={(e) => setPeriod(e.target.value as Period)} aria-label="Período">
          {Object.entries(PERIODS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Input type="search" placeholder="Buscar por número (ej.: 123)" value={number} onChange={(e) => setNumber(e.target.value)} aria-label="Buscar por número" />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Vendido" icon={CashIcon} value={money(summary.sold)} foot={`${summary.count} ${summary.count === 1 ? 'venta' : 'ventas'}`} />
        <Stat label="Devuelto" icon={ReceiptIcon} tone={summary.returned ? 'red' : 'slate'} value={money(summary.returned)} />
        <Stat label="Neto" icon={TrendIcon} tone="green" value={money(summary.sold - summary.returned)} />
      </div>

      {!list.online && <Alert tone="warning">Sin conexión: se muestran solo las ventas de este dispositivo.</Alert>}
      {list.serverError && <Alert tone="warning">{list.serverError}</Alert>}

      <Card flush className="divide-y divide-slate-100">
        {list.loading && (
          <div className="flex justify-center p-6 text-slate-500">
            <Spinner />
          </div>
        )}
        {!list.loading && list.sales.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No hay ventas en este período.</p>}
        {list.sales.map((s) => (
          <button key={s.id} type="button" onClick={() => setOpenId(s.id)} className="flex w-full items-center gap-3 p-3 text-left hover:bg-slate-50 sm:px-4">
            <span
              aria-hidden="true"
              className={cx(
                'flex size-9 shrink-0 items-center justify-center rounded-lg',
                s.kind === 'return' ? 'bg-red-50 text-red-700 ring-1 ring-red-200' : 'bg-slate-100 text-slate-500',
              )}
            >
              <ReceiptIcon className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {s.kind === 'return' ? 'Devolución' : 'Venta'} {s.number}
              </p>
              <p className="truncate text-sm text-slate-500">
                {dateFormat.format(new Date(s.occurred_at))} · {person(s.seller_id)} · {s.payments.map((p) => PAYMENT_METHODS[p.method].label).join(' + ')}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className={cx('font-semibold tabular-nums', s.kind === 'return' && 'text-red-700')}>
                {s.kind === 'return' ? '−' : ''}
                {money(s.total)}
              </p>
              {list.pending.has(s.id) && <Badge tone="amber">Sin enviar</Badge>}
            </div>
          </button>
        ))}
      </Card>

      <SaleDetail sale={open} person={person} pending={!!open && list.pending.has(open.id)} unitOf={unitOf} onClose={() => setOpenId(null)} onOpen={setOpenId} sales={list.sales} />
    </div>
  );
}

function SaleDetail({
  sale,
  sales,
  person,
  pending,
  unitOf,
  onClose,
  onOpen,
}: {
  sale: SaleRow | undefined;
  sales: SaleRow[];
  person: (id: string) => string;
  pending: boolean;
  unitOf: (variantId: string) => UnitType;
  onClose: () => void;
  onOpen: (id: string) => void;
}) {
  const { can } = useSession();
  const canWrite = useCanWrite();
  const money = useMoney();
  const ticket = useTicket();
  const [returns, setReturns] = useState<SaleRow[]>([]);
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    setReturns([]);
    setReturning(false);
    if (!sale || sale.kind !== 'sale') return;
    let cancelled = false;
    void fetchReturns(sale.id, unitOf).then((r) => !cancelled && setReturns(r));
    return () => {
      cancelled = true;
    };
  }, [sale?.id, unitOf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Las devoluciones recién hechas en este dispositivo aparecen al instante.
  const allReturns = useMemo(() => {
    if (!sale) return [];
    const byId = new Map(returns.map((r) => [r.id, r]));
    for (const s of sales) if (s.original_sale_id === sale.id) byId.set(s.id, s);
    return [...byId.values()];
  }, [returns, sales, sale]);

  const original = sale?.kind === 'return' ? sales.find((s) => s.id === sale.original_sale_id) : undefined;
  const returnedQty = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of allReturns) for (const i of r.items) if (i.original_item_id) map.set(i.original_item_id, (map.get(i.original_item_id) ?? 0) + i.qty_milli);
    return map;
  }, [allReturns]);
  const canReturn = !!sale && sale.kind === 'sale' && canWrite && can('void_sales') && [...returnableQty(sale, returnedQty).values()].some((q) => q > 0);

  return (
    <>
      <Dialog
        open={!!sale && !returning}
        onClose={onClose}
        title={sale ? `${sale.kind === 'return' ? 'Devolución' : 'Venta'} ${sale.number}` : ''}
        footer={
          sale && (
            <>
              <Button variant="secondary" onClick={() => ticket.whatsapp(sale, original?.number)}>
                WhatsApp
              </Button>
              <Button variant="secondary" onClick={() => ticket.print(sale, original?.number)}>
                Imprimir
              </Button>
              {canReturn && (
                <Button variant="danger" onClick={() => setReturning(true)}>
                  Devolver
                </Button>
              )}
            </>
          )
        }
      >
        {sale && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              {dateFormat.format(new Date(sale.occurred_at))} · {person(sale.seller_id)}
              {pending && <span className="ml-2 text-amber-700">· todavía no se envió al servidor</span>}
            </p>
            {sale.kind === 'return' && sale.original_sale_id && (
              <p>
                Devolución de la venta{' '}
                {original ? (
                  <button type="button" className="font-medium text-brand" onClick={() => onOpen(original.id)}>
                    {original.number}
                  </button>
                ) : (
                  'original'
                )}
              </p>
            )}
            <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
              {sale.items.map((item) => (
                <li key={item.id} className="flex justify-between gap-3 px-3 py-2">
                  <span className="min-w-0">
                    <span className="block truncate">{item.description}</span>
                    <span className="text-xs text-slate-500">
                      {lineDetail(item, money)}
                      {item.discount > 0 && ` · desc. ${money(item.discount)}`}
                      {returnedQty.get(item.id) ? ` · devuelto ${formatQty(returnedQty.get(item.id)!, item.unit_type)}` : ''}
                    </span>
                  </span>
                  <span className="shrink-0 tabular-nums">{money(item.total)}</span>
                </li>
              ))}
            </ul>
            <div className="space-y-1">
              {sale.discount_total > 0 && <Line label="Descuentos" value={`−${money(sale.discount_total)}`} />}
              {sale.payments.map((p) => (
                <Line
                  key={p.id}
                  label={`${PAYMENT_METHODS[p.method].label}${p.adjustment ? ` (${p.adjustment > 0 ? 'recargo' : 'descuento'} ${money(Math.abs(p.adjustment))})` : ''}`}
                  value={money(p.amount)}
                />
              ))}
              <Line label={sale.kind === 'return' ? 'Total devuelto' : 'Total'} value={money(sale.total)} strong />
            </div>
            {allReturns.length > 0 && (
              <div>
                <p className="font-medium">Devoluciones</p>
                <ul className="mt-1 space-y-1">
                  {allReturns.map((r) => (
                    <li key={r.id} className="flex justify-between">
                      <button type="button" className="text-brand" onClick={() => onOpen(r.id)}>
                        {r.number} · {dateFormat.format(new Date(r.occurred_at))}
                      </button>
                      <span className="tabular-nums text-red-700">−{money(r.total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Dialog>
      {sale && returning && (
        <ReturnDialog
          sale={sale}
          previous={allReturns}
          onClose={() => setReturning(false)}
          onDone={(r) => {
            setReturning(false);
            onOpen(r.id);
          }}
        />
      )}
    </>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cx('flex justify-between', strong && 'text-base font-semibold')}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function ReturnDialog({ sale, previous, onClose, onDone }: { sale: SaleRow; previous: SaleRow[]; onClose: () => void; onDone: (ret: SaleRow) => void }) {
  const { business, device, membership, writer } = useSession();
  const money = useMoney();
  const settings = usePosSettings();
  const returned = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of previous) for (const i of r.items) if (i.original_item_id) map.set(i.original_item_id, (map.get(i.original_item_id) ?? 0) + i.qty_milli);
    return map;
  }, [previous]);
  const left = returnableQty(sale, returned);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [method, setMethod] = useState<PaymentMethod>(mainMethod(sale));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cashSession = useOpenSession();
  const methods = PAYMENT_METHOD_KEYS.filter(
    (m) => (m === 'account' ? !!sale.customer_id : settings.payments[m].enabled) || sale.payments.some((p) => p.method === m),
  );
  const cashMissing = method === 'cash' && settings.require_open_cash && cashSession === null;
  const selections = sale.items.map((i) => ({ itemId: i.id, qtyMilli: (qty[i.id] ?? '').trim() ? (parseQty(qty[i.id]!, i.unit_type) ?? -1) : 0 }));
  const invalid = selections.some((s) => s.qtyMilli < 0 || s.qtyMilli > (left.get(s.itemId) ?? 0));

  let preview: ReturnType<typeof buildReturn> | null = null;
  let previewError: string | null = null;
  if (!invalid && selections.some((s) => s.qtyMilli > 0)) {
    try {
      preview = buildReturn(sale, selections, previous, method, {
        businessId: business.id,
        deviceId: device.id,
        sellerId: membership.user_id,
        cashSessionId: cashSession?.id ?? null,
        now: new Date().toISOString(),
        newId: uuidv7,
      });
    } catch (e) {
      previewError = errorMessage(e);
    }
  }

  const confirm = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await recordSaleLocally(db, { ...preview, occurred_at: new Date().toISOString(), created_at: new Date().toISOString() }, device.code, writer);
      requestSync(0);
      onDone(saved);
    } catch (e) {
      setError(e instanceof SaleError ? e.message : errorMessage(e));
      setBusy(false);
    }
  };

  const fmt = (milli: number) => String(milli / 1000).replace('.', ',');

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Devolver de la venta ${sale.number}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="danger" disabled={!preview || cashMissing} loading={busy} onClick={confirm}>
            Registrar devolución
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
          {sale.items.map((item) => {
            const max = left.get(item.id) ?? 0;
            const value = qty[item.id] ?? '';
            const parsed = value.trim() ? parseQty(value, item.unit_type) : 0;
            return (
              <li key={item.id} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{item.description}</p>
                  <p className="text-xs text-slate-500">
                    Vendido {formatQty(item.qty_milli, item.unit_type)}
                    {max < item.qty_milli && ` · ya devuelto ${formatQty(item.qty_milli - max, item.unit_type)}`}
                  </p>
                </div>
                {max > 0 ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <div className="w-20">
                      <Input
                        inputMode="decimal"
                        compact
                        placeholder="0"
                        aria-label={`Cantidad a devolver de ${item.description}`}
                        value={value}
                        invalid={parsed === null || (parsed ?? 0) > max}
                        onChange={(e) => setQty((q) => ({ ...q, [item.id]: e.target.value }))}
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setQty((q) => ({ ...q, [item.id]: fmt(max) }))}>
                      Todo
                    </Button>
                  </div>
                ) : (
                  <span className="text-xs text-slate-500">ya devuelto</span>
                )}
              </li>
            );
          })}
        </ul>
        <div className="flex items-center gap-3">
          <span className="shrink-0 text-slate-600">Se devuelve en</span>
          <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} compact aria-label="Medio de devolución">
            {methods.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHODS[m].label}
              </option>
            ))}
          </Select>
        </div>
        {preview && (
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="flex justify-between text-base font-semibold">
              <span>A devolver</span>
              <span className="tabular-nums">{money(preview.total)}</span>
            </div>
            {preview.adjustment_total !== 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Incluye {money(preview.adjustment_total)} de {preview.adjustment_total > 0 ? 'recargo' : 'descuento'} por medio de pago de la venta original.
              </p>
            )}
            <p className="mt-1 text-xs text-slate-500">Los productos vuelven al stock.</p>
          </div>
        )}
        {cashMissing && <Alert tone="warning">Para devolver en efectivo abrí la caja (Caja → Abrir caja), así la devolución queda en el arqueo.</Alert>}
        {(previewError || error) && <Alert tone="error">{previewError ?? error}</Alert>}
      </div>
    </Dialog>
  );
}
